/**
 * Un bloc qu'on peut replier — chunk 9.BF.1.
 *
 * ## Pourquoi `<details>` et pas un état React
 *
 * Le repli est un comportement que le navigateur connaît : clavier, lecteurs
 * d'écran, recherche dans la page (Chrome ouvre les `<details>` fermés pour y
 * trouver du texte). Le reproduire avec `useState` + `aria-expanded` demande
 * d'écrire à la main ce qui existe déjà, et de l'écrire juste.
 *
 * ## Ce que le repli sert à dire
 *
 * Ce qui est replié n'est pas moins important : c'est moins urgent. Les détails
 * administratifs d'un dossier ne changent pas dix fois par jour, et les laisser
 * dépliés obligeait à les traverser à chaque visite pour atteindre ce qui bouge.
 */

import type { ReactNode } from "react";

export function BlocRepliable(props: {
  readonly titre: string;
  /** Court complément affiché à droite du titre — un compte, une date. */
  readonly indice?: string;
  /** Ouvert d'emblée quand le contenu appelle une action. */
  readonly ouvertParDefaut?: boolean;
  readonly children: ReactNode;
}) {
  return (
    <details
      open={props.ouvertParDefaut ?? false}
      className="group rounded-xl border border-slate-200 bg-white shadow-sm"
    >
      <summary className="focus-visible:ring-brand-500 flex cursor-pointer touch-manipulation list-none items-center justify-between gap-3 rounded-xl px-5 py-4 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2">
        <span className="text-base font-semibold text-slate-900">{props.titre}</span>
        <span className="flex shrink-0 items-center gap-2">
          {props.indice && <span className="text-sm text-slate-500">{props.indice}</span>}
          <svg
            aria-hidden="true"
            viewBox="0 0 20 20"
            className="h-4 w-4 text-slate-400 transition-transform group-open:rotate-180 motion-reduce:transition-none"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.17l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      </summary>
      <div className="border-t border-slate-100 px-5 py-5">{props.children}</div>
    </details>
  );
}
